import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook that manages a timed message (success/error) with automatic cleanup.
 * Prevents memory leaks by clearing timeouts on unmount.
 *
 * @param {number} defaultDuration - Default duration in ms before message clears (default 3000)
 * @returns {[string, function]} - [message, show(msg, duration?)]
 */
const useTimedMessage = (defaultDuration = 3000) => {
  const [message, setMessage] = useState('');
  const timerRef = useRef(null);

  const show = useCallback((msg, duration) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setMessage(msg);
    timerRef.current = setTimeout(() => {
      setMessage('');
      timerRef.current = null;
    }, duration ?? defaultDuration);
  }, [defaultDuration]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return [message, show, setMessage];
};

export default useTimedMessage;
